import React, { useState, useEffect } from 'react';
import { format } from 'date-fns';
import { Users, Hash, MessageSquare, Search, Plus } from 'lucide-react';
import { Channel } from '../../types/messages';
import { collection, query, where, getDocs, orderBy, limit, onSnapshot, doc, getDoc } from 'firebase/firestore';
import { db, auth } from '../../firebase';
import StartDirectMessageModal from './StartDirectMessageModal';

interface DirectMessage {
  id: string;
  userId: string;
  userName: string;
  lastMessage?: {
    content: string;
    createdAt: string;
  };
}

interface ChannelListProps {
  channels: Channel[];
  selectedChannelId?: string;
  onSelectChannel: (channelId: string) => void;
  onCreateChannel: () => void;
  onStartDirectMessage: (userId: string, userName: string) => void;
}

export default function ChannelList({ channels, selectedChannelId, onSelectChannel, onCreateChannel, onStartDirectMessage }: ChannelListProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [directMessages, setDirectMessages] = useState<DirectMessage[]>([]);
  const [loading, setLoading] = useState(true);
  const [showStartDMModal, setShowStartDMModal] = useState(false);
  const [userNames, setUserNames] = useState<Record<string, string>>({});

  useEffect(() => {
    if (!auth.currentUser) return;

    // Use onSnapshot to listen for real-time updates to messages
    const messagesQuery = query(
      collection(db, 'messages'),
      where('participants', 'array-contains', auth.currentUser.uid),
      orderBy('createdAt', 'desc')
    );

    const unsubscribe = onSnapshot(messagesQuery, async (messageSnapshot) => {
      try {
        // Get unique users from messages
        const userIds = new Set<string>();
        messageSnapshot.docs.forEach(doc => {
          const data = doc.data();
          data.participants.forEach((participantId: string) => {
            if (participantId !== auth.currentUser?.uid) {
              userIds.add(participantId);
            }
          });
        });

        // Get user details and their last messages
        const dmThreads = await Promise.all(
          Array.from(userIds).map(async (userId) => {
            // Get user details
            const userDoc = await getDocs(
              query(collection(db, 'users'), where('__name__', '==', userId))
            );
            
            if (!userDoc.empty) {
              const userData = userDoc.docs[0].data();
              
              // Find the last message for this user from the already fetched messages
              const lastMessage = messageSnapshot.docs
                .find(doc => {
                  const data = doc.data();
                  return data.participants.includes(userId);
                });

              const lastMessageData = lastMessage?.data();
              
              return {
                id: `dm_${userId}`,
                userId,
                userName: userData.fullName || userData.displayName || userData.email?.split('@')[0] || 'Unknown User',
                lastMessage: lastMessageData ? {
                  content: lastMessageData.content,
                  createdAt: lastMessageData.createdAt
                } : undefined
              };
            }
            return null;
          })
        );

        setDirectMessages(dmThreads.filter((thread): thread is DirectMessage => thread !== null));
      } catch (error) {
        console.error('Error loading direct messages:', error);
      } finally {
        setLoading(false);
      }
    });

    return () => unsubscribe();
  }, []);

  // Fetch user names for channel last message senders
  useEffect(() => {
    const fetchUserNames = async () => {
      const senderIds = channels
        .filter(channel => channel.lastMessage?.senderId)
        .map(channel => channel.lastMessage!.senderId)
        .filter(senderId => !userNames[senderId]); // Only fetch names we don't have

      if (senderIds.length === 0) return;

      const namePromises = senderIds.map(async (senderId) => {
        try {
          const userDoc = await getDoc(doc(db, 'users', senderId));
          if (userDoc.exists()) {
            const userData = userDoc.data();
            const name = userData.fullName || userData.displayName || userData.email?.split('@')[0] || 'Unknown User';
            return [senderId, name] as [string, string];
          }
        } catch (error) {
          console.error('Error fetching user:', error);
        }
        return [senderId, 'Unknown User'] as [string, string];
      });

      const results = await Promise.all(namePromises);
      const newUserNames = Object.fromEntries(results);
      setUserNames(prev => ({ ...prev, ...newUserNames }));
    };

    fetchUserNames();
  }, [channels, userNames]);

  const filteredChannels = channels.filter(channel =>
    channel.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const filteredDMs = directMessages.filter(dm =>
    dm.userName.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (loading) {
    return (
      <div className="w-64 border-r border-border-light p-4">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-background rounded w-full" />
          <div className="h-10 bg-background rounded w-full" />
          <div className="space-y-2">
            {[1, 2, 3].map(i => (
              <div key={i} className="h-12 bg-background rounded w-full" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="w-64 border-r border-border-light overflow-y-auto flex flex-col">
      <div className="p-4 border-b border-border-light">
        <div className="relative mb-4">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-text-secondary" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search..."
            className="w-full pl-9 pr-4 py-2 text-sm bg-background rounded-lg border border-border-light 
                     focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 outline-none"
          />
        </div>
      </div>
      
      <div className="flex-1 overflow-y-auto">
        {/* Channels Section */}
        <div className="p-2">
          <div className="flex items-center justify-between px-2 py-1 text-text-secondary text-sm">
            <div className="flex items-center gap-2">
              <Users className="w-4 h-4" />
              <span>Channels</span>
            </div>
            <button
              onClick={onCreateChannel}
              className="p-1 hover:text-primary-500 transition-colors rounded-lg"
              title="Create Channel"
            >
              <Plus className="w-4 h-4" />
            </button>
          </div>
          
          {filteredChannels.map((channel) => (
            <button
              key={channel.id}
              onClick={() => onSelectChannel(channel.id)}
              className={`w-full p-3 rounded-lg text-left transition-colors ${
                selectedChannelId === channel.id
                  ? 'bg-primary-50 text-primary-600'
                  : 'hover:bg-background text-text-primary'
              }`}
            >
              <div className="flex items-center gap-2">
                <Hash className="w-4 h-4" />
                <span className="font-medium">{channel.name}</span>
              </div>
              
              {channel.lastMessage && (
                <div className="mt-1 text-sm">
                  <p className="text-text-secondary truncate">
                    {channel.lastMessage.senderId === auth.currentUser?.uid 
                      ? `You: ${channel.lastMessage.content}`
                      : `${userNames[channel.lastMessage.senderId] || 'Loading...'}: ${channel.lastMessage.content}`
                    }
                  </p>
                  <p className="text-xs text-text-muted">
                    {(() => {
                      if (!channel.lastMessage.createdAt) return 'Just now';
                      try {
                        const date = new Date(channel.lastMessage.createdAt);
                        if (isNaN(date.getTime())) return 'Just now';
                        return format(date, 'p');
                      } catch {
                        return 'Just now';
                      }
                    })()}
                  </p>
                </div>
              )}
            </button>
          ))}
        </div>

        {/* Direct Messages Section */}
        <div className="p-2">
          <div className="flex items-center justify-between px-2 py-1 text-text-secondary text-sm">
            <div className="flex items-center gap-2">
              <MessageSquare className="w-4 h-4" />
              <span>Direct Messages</span>
            </div>
            <button
              onClick={() => setShowStartDMModal(true)}
              className="p-1 hover:text-primary-500 transition-colors rounded-lg"
              title="Start Direct Message"
            >
              <Plus className="w-4 h-4" />
            </button>
          </div>
          
          {filteredDMs.map((dm) => (
            <button
              key={dm.id}
              onClick={() => onStartDirectMessage(dm.userId, dm.userName)}
              className={`w-full p-3 rounded-lg text-left transition-colors ${
                selectedChannelId === dm.id
                  ? 'bg-primary-50 text-primary-600'
                  : 'hover:bg-background text-text-primary'
              }`}
            >
              <div className="flex items-center gap-2">
                <div className="w-6 h-6 rounded-full bg-primary-100 flex items-center justify-center">
                  <span className="text-xs font-medium text-primary-700">
                    {dm.userName.charAt(0).toUpperCase()}
                  </span>
                </div>
                <span className="font-medium">{dm.userName}</span>
              </div>
              
              {dm.lastMessage && (
                <div className="mt-1 text-sm">
                  <p className="text-text-secondary truncate">{dm.lastMessage.content}</p>
                  <p className="text-xs text-text-muted">
                    {(() => {
                      if (!dm.lastMessage.createdAt) return 'Just now';
                      try {
                        const date = new Date(dm.lastMessage.createdAt);
                        if (isNaN(date.getTime())) return 'Just now';
                        return format(date, 'p');
                      } catch {
                        return 'Just now';
                      }
                    })()}
                  </p>
                </div>
              )}
            </button>
          ))}
        </div>
      </div>

      {showStartDMModal && (
        <StartDirectMessageModal
          onClose={() => setShowStartDMModal(false)}
          onSelectUser={(userId, userName) => {
            onStartDirectMessage(userId, userName);
            setShowStartDMModal(false);
          }}
        />
      )}
    </div>
  );
}